import { Ref, useImperativeHandle, useState } from 'react';
import { useModal } from '@/hooks/useModal';
import { AddContactModal } from './AddContactModal';
import { ImportContactsConfirmationModal } from './ImportContactsConfirmationModal';
import { useImportContactsCSV } from '../queries';
import { useUser } from '@/queries/useUser';

const MAX_CONTACT_COUNT = 5000;
export interface AddContactFlowRef {
    open: () => void;
}

export type ImportErrorCode = 'missing-fields' | 'row-count-exceeded' | 'partially-imported' | null;

export interface ImportErrorState {
    code: ImportErrorCode;
    message?: string;
    hint?: string;
}

/**
 * Manages the entire "Add Contact" experience including:
 * - Add Contact Modal (manual entry + CSV upload)
 * - Import Confirmation Modal
 * - File handling and import logic
 */
export const AddContactFlow = ({ ref }: { ref?: Ref<AddContactFlowRef> }) => {
    const addContactModal = useModal();
    const confirmationModal = useModal();
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [contactCount, setContactCount] = useState(0);
    const [importError, setImportError] = useState<ImportErrorState>({ code: null });
    const { user } = useUser();
    const { mutate: importCSV, isPending: isImporting } = useImportContactsCSV();

    // Expose methods to parent component
    useImperativeHandle(
        ref,
        () =>
            ({
                open: () => {
                    addContactModal.open();
                },
            }) as AddContactFlowRef
    );

    const handleFileSelected = (file: File, count: number) => {
        // Reset error state when new file is selected
        setImportError({ code: null });
        setSelectedFile(file);
        setContactCount(count);
        confirmationModal.open();
    };

    const handleConfirmImport = (tag: string) => {
        if (!selectedFile) return;

        // Check row count limit before importing
        const ignoreImportLimits = user?.data?.ignore_import_limits;
        const exceedsLimit = contactCount > MAX_CONTACT_COUNT && !ignoreImportLimits;
        if (exceedsLimit) {
            // Show error in confirmation modal (don't close it)
            setImportError({
                code: 'row-count-exceeded',
                message: undefined,
                hint: 'We had trouble importing your contacts. <a href="https://help.stan.store/article/162-how-to-import-your-existing-email-list-into-stan" target="_blank" rel="noopener noreferrer">See here.</a>',
            });
            return;
        }

        // Reset error state before import
        setImportError({ code: null });

        importCSV(
            { file: selectedFile, tag: tag || undefined },
            {
                onSuccess: () => {
                    setSelectedFile(null);
                    setContactCount(0);
                    setImportError({ code: null });
                    confirmationModal.close();
                },
                onError: (error: { response?: { data?: { code?: string; message?: string } } }) => {
                    const errorCode = error?.response?.data?.code;
                    const errorMessage = error?.response?.data?.message;

                    // Validate error code matches our expected types
                    const validErrorCode: ImportErrorCode =
                        errorCode === 'missing-fields' ||
                        errorCode === 'row-count-exceeded' ||
                        errorCode === 'partially-imported'
                            ? errorCode
                            : null;

                    // Show error in confirmation modal (don't close it, allows retry)
                    setImportError({
                        code: validErrorCode,
                        message: errorMessage,
                        hint: 'For more information: <a href="https://help.stan.store/article/162-how-to-import-your-existing-email-list-into-stan" target="_blank" rel="noopener noreferrer">See here.</a>',
                    });
                },
            }
        );
    };

    const handleConfirmationClose = () => {
        setSelectedFile(null);
        setContactCount(0);
        setImportError({ code: null });
        confirmationModal.close();
    };

    return (
        <>
            <AddContactModal
                isOpen={addContactModal.isOpen}
                onClose={addContactModal.close}
                onFileSelected={handleFileSelected}
            />
            <ImportContactsConfirmationModal
                isOpen={confirmationModal.isOpen}
                onClose={handleConfirmationClose}
                onConfirm={handleConfirmImport}
                onFileSelected={handleFileSelected}
                contactCount={contactCount}
                isImporting={isImporting}
                importError={importError}
            />
        </>
    );
};

AddContactFlow.displayName = 'AddContactFlow';
